import React, { useMemo } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {
  Dashboard,
  Login,
  Register,
  CompanySelect,
  CompanyManager,
  ServiceStationManager,
  PositionManager,
  EmployeeManager,
  ReasonManager,
  BenefitManager,
  JourneyManager,
  CustomerManager,
  SupplierManager,
  AccountantManager,
  BankAccountManager,
  CostCenterManager,
  CategoryManager,
  TransactionManager,
  SubUserManager,
  FinancialOverview,
  FileManager,
  AdjustmentManager,
  ExecutedPoints,
  TimeKeepingOverview,
  InspectionConfig,
  InspectionOverview,
  VehicleManager,
  MachineryManager,
  InspectionExecution,
  InspectionPublicView,
  SpecificDocumentManager,
  ServiceManager,
  ServiceOrderRegistrations,
  ServiceOrderSchedule,
  ServiceOrderOverview,
  RiskManager,
  RiskEmployeeFormPage,
  HolidayManager,
  BusinessAgent,
  WorkSchedules,
  Timesheet,
} from './pages';
import AbsenceManager from './pages/AbsenceManager';
import RoundConfig from './pages/Rounds/Config';
import PermissionRoute from './components/PermissionRoute';

function PrivateRoute() {
  const token = localStorage.getItem('authToken');
  const isAuthenticated = !!token;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

function App() {
  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/risk/public/:token" element={<RiskEmployeeFormPage />} />
          <Route path="/inspection/public/:token" element={<InspectionPublicView />} />

          <Route element={<PrivateRoute />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/business-agent" element={<BusinessAgent />} />
            <Route path="/select-company" element={<CompanySelect />} />

            <Route element={<PermissionRoute module="companies" />}>
              <Route path="/companies" element={<CompanyManager />} />
            </Route>

            <Route element={<PermissionRoute module="sub_users" />}>
              <Route path="/sub-users" element={<SubUserManager />} />
            </Route>

            <Route element={<PermissionRoute module="management" />}>
              <Route path="/stations" element={<ServiceStationManager />} />
              <Route path="/roles" element={<PositionManager />} />
              <Route path="/reasons" element={<ReasonManager />} />
              <Route path="/employees" element={<EmployeeManager />} />
              <Route path="/journeys" element={<JourneyManager />} />
              <Route path="/benefits" element={<BenefitManager />} />
              <Route path="/files" element={<FileManager />} />
              <Route path="/vehicles" element={<VehicleManager />} />
              <Route path="/machineries" element={<MachineryManager />} />
              <Route path="/specific-documents" element={<SpecificDocumentManager />} />
              <Route path="/risk" element={<RiskManager />} />
              <Route path="/holidays" element={<HolidayManager />} />
            </Route>

            <Route element={<PermissionRoute module="financial" />}>
              <Route path="/financial/customers" element={<CustomerManager />} />
              <Route path="/financial/suppliers" element={<SupplierManager />} />
              <Route path="/financial/accountants" element={<AccountantManager />} />
              <Route path="/financial/accounts" element={<BankAccountManager />} />
              <Route path="/financial/cost-centers" element={<CostCenterManager />} />
              <Route path="/financial/categories" element={<CategoryManager />} />
              <Route path="/financial/transactions" element={<TransactionManager />} />
              <Route path="/financial/overview" element={<FinancialOverview />} />
            </Route>

            <Route element={<PermissionRoute module="time_keeping" />}>
              <Route path="/time-keeping/adjustments" element={<AdjustmentManager />} />
              <Route path="/time-keeping/work-schedules" element={<WorkSchedules />} />
              <Route path="/time-keeping/executed-points" element={<ExecutedPoints />} />
              <Route path="/time-keeping/overview" element={<TimeKeepingOverview />} />
              <Route path="/time-keeping/absences" element={<AbsenceManager />} />
              <Route path="/time-keeping/timesheet" element={<Timesheet />} />
            </Route>

            <Route element={<PermissionRoute module="patrol" />}>
              <Route path="/rounds/config" element={<RoundConfig />} />
            </Route>

            <Route element={<PermissionRoute module="inspections" />}>
              <Route path="/inspections/overview" element={<InspectionOverview />} />
              <Route path="/inspections/config" element={<InspectionConfig />} />
              <Route path="/inspections/executed" element={<InspectionExecution />} />
            </Route>

            <Route element={<PermissionRoute module="service_orders" />}>
              <Route path="/service-orders/services" element={<ServiceManager />} />
              <Route path="/service-orders/registrations" element={<ServiceOrderRegistrations />} />
              <Route path="/service-orders/schedule" element={<ServiceOrderSchedule />} />
              <Route path="/service-orders/overview" element={<ServiceOrderOverview />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </>
  );
}

export default App;
